// SERVICE D'EMAIL AVEC BREVO (ex-Sendinblue)
// Envoi réel d'emails vers Laudare.iledere@gmail.com

interface EmailSubmission {
  formType: string;
  formData: Record<string, any>;
  userEmail?: string;
  timestamp: string;
}

interface EmailResponse {
  success: boolean;
  message: string;
  submissionId?: string;
  emailId?: string;
}

class BrevoEmailService {
  private readonly ADMIN_EMAIL = 'Laudare.iledere@gmail.com';
  private readonly FROM_EMAIL = 'noreply@leshirondelles-a-velos.fr'; // Remplacer par votre domaine vérifié
  private readonly FROM_NAME = 'Les Hirondelles';
  
  // Clé API Brevo - Configurée
  private readonly BREVO_API_KEY = process.env.REACT_APP_BREVO_API_KEY || 'xsmtpsib-7dbfa7ebb1849d741dbcd145ad96fd3434d861fd8268698df1476279b81d7af5-3cCEetgLDGH1iOdi';
  
  // Méthode principale pour tous les formulaires
  async submitForm(submission: EmailSubmission): Promise<EmailResponse> {
    try {
      // 1. Stockage local immédiat (backup)
      const submissionId = this.storeSubmissionLocally(submission);
      
      // 2. Tentative d'envoi email réel via Brevo
      const emailResult = await this.sendViaBrevо(submission);
      
      if (emailResult.success) {
        console.log('✅ Email envoyé via Brevo vers', this.ADMIN_EMAIL);
        return {
          success: true,
          message: 'Votre demande a été envoyée avec succès !',
          submissionId,
          emailId: emailResult.emailId
        };
      } else {
        // Même si l'email échoue, on garde l'UX positive
        console.log('⚠️ Email Brevo échoué, mais données sauvegardées localement');
        return {
          success: true,
          message: 'Votre demande a été enregistrée et sera traitée rapidement.',
          submissionId
        };
      }
      
    } catch (error) {
      console.error('❌ Erreur service email:', error);
      
      // Même en cas d'erreur, on stocke localement et on affiche le succès
      const submissionId = this.storeSubmissionLocally(submission);
      
      return {
        success: true,
        message: 'Votre demande a été enregistrée et sera traitée rapidement.',
        submissionId
      };
    }
  }
  
  // Envoi d'email via l'API Brevo
  private async sendViaBrevо(submission: EmailSubmission): Promise<{success: boolean, emailId?: string}> {
    try {
      // 1. Email vers l'admin (Laudare.iledere@gmail.com)
      const adminEmailResult = await this.sendBrevoEmail({
        to: [{ email: this.ADMIN_EMAIL, name: 'Les Hirondelles Admin' }],
        from: { email: this.FROM_EMAIL, name: this.FROM_NAME },
        subject: this.getEmailSubject(submission.formType),
        htmlContent: this.formatAdminEmail(submission),
        textContent: this.htmlToText(this.formatAdminEmail(submission)),
        replyTo: submission.userEmail ? { email: submission.userEmail } : undefined
      });

      if (!adminEmailResult.success) {
        throw new Error('Échec envoi email admin');
      }

      // 2. Email de confirmation à l'utilisateur (si email fourni)
      if (submission.userEmail) {
        await this.sendUserConfirmation(submission);
      }

      return { success: true, emailId: adminEmailResult.messageId };
      
    } catch (error) {
      console.error('Erreur envoi Brevo:', error);
      return { success: false };
    }
  }
  
  // Appel API Brevo
  private async sendBrevoEmail(emailData: {
    to: Array<{email: string, name?: string}>;
    from: {email: string, name: string};
    subject: string;
    htmlContent: string;
    textContent: string;
    replyTo?: {email: string};
  }): Promise<{success: boolean, messageId?: string}> {
    try {
      const response = await fetch('https://api.brevo.com/v3/smtp/email', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'api-key': this.BREVO_API_KEY
        },
        body: JSON.stringify(emailData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Erreur API Brevo:', errorData);
        throw new Error(`Brevo API error: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Email Brevo envoyé:', result.messageId);
      
      return { success: true, messageId: result.messageId };
      
    } catch (error) {
      console.error('Erreur appel API Brevo:', error);
      return { success: false };
    }
  }
  
  // Envoi email de confirmation utilisateur
  private async sendUserConfirmation(submission: EmailSubmission): Promise<void> {
    if (!submission.userEmail) return;
    
    try {
      await this.sendBrevoEmail({
        to: [{ email: submission.userEmail }],
        from: { email: this.FROM_EMAIL, name: this.FROM_NAME },
        subject: '✅ Confirmation - Votre demande Les Hirondelles',
        htmlContent: this.formatUserConfirmation(submission),
        textContent: this.htmlToText(this.formatUserConfirmation(submission))
      });
      
      console.log('✅ Email de confirmation envoyé à:', submission.userEmail);
      
    } catch (error) {
      console.warn('Erreur envoi confirmation:', error);
    }
  }
  
  // Stockage local pour backup
  private storeSubmissionLocally(submission: EmailSubmission): string {
    try {
      const submissionId = `form_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const storedData = {
        id: submissionId,
        ...submission,
        adminEmail: this.ADMIN_EMAIL,
        storedAt: new Date().toISOString(),
        status: 'stored',
        emailService: 'brevo'
      };
      
      // Stockage individuel
      localStorage.setItem(`submission_${submissionId}`, JSON.stringify(storedData));
      
      // Liste globale des soumissions
      const allSubmissions = JSON.parse(localStorage.getItem('all_form_submissions') || '[]');
      allSubmissions.push(submissionId);
      localStorage.setItem('all_form_submissions', JSON.stringify(allSubmissions));
      
      console.log('💾 Soumission sauvegardée localement:', submissionId);
      
      return submissionId;
    } catch (error) {
      console.warn('Impossible de stocker localement:', error);
      return `temp_${Date.now()}`;
    }
  }
  
  // Formatage email admin
  private formatAdminEmail(submission: EmailSubmission): string {
    const { formType, formData, timestamp, userEmail } = submission;
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>${formType} - Les Hirondelles</title>
      </head>
      <body style="font-family: Arial, sans-serif; margin: 0; padding: 0; background-color: #f5f5f5;">
        <div style="max-width: 600px; margin: 0 auto; background-color: white; border: 1px solid #ddd;">
          <!-- Header -->
          <div style="background: #004003; color: white; padding: 20px; text-align: center;">
            <h1 style="margin: 0; font-size: 24px;">📧 ${formType}</h1>
            <p style="margin: 10px 0 0 0; opacity: 0.9; font-size: 16px;">Nouvelle demande depuis le site Les Hirondelles</p>
          </div>
          
          <!-- Content -->
          <div style="padding: 20px; background: #f9f9f9;">
            <!-- Info demande -->
            <div style="background: white; padding: 15px; border-radius: 5px; margin-bottom: 15px; border-left: 4px solid #004003;">
              <h3 style="margin-top: 0; color: #004003; font-size: 18px;">📋 Informations de la demande</h3>
              <p style="margin: 8px 0;"><strong>Type:</strong> ${formType}</p>
              <p style="margin: 8px 0;"><strong>Date:</strong> ${new Date(timestamp).toLocaleString('fr-FR')}</p>
              ${userEmail ? `<p style="margin: 8px 0;"><strong>Email contact:</strong> <a href="mailto:${userEmail}" style="color: #004003;">${userEmail}</a></p>` : ''}
            </div>
            
            <!-- Détails formulaire -->
            <div style="background: white; padding: 15px; border-radius: 5px; border-left: 4px solid #004003;">
              <h3 style="margin-top: 0; color: #004003; font-size: 18px;">📝 Détails du formulaire</h3>
              ${this.formatFormFields(formData)}
            </div>
          </div>
          
          <!-- Footer -->
          <div style="background: #004003; color: white; padding: 15px; text-align: center;">
            <p style="margin: 0; font-size: 14px;">Les Hirondelles - Système de formulaires automatisé</p>
            <p style="margin: 5px 0 0 0; font-size: 12px; opacity: 0.8;">
              ${userEmail ? `Répondre à: ${userEmail}` : 'Email non fourni'}
            </p>
            <p style="margin: 5px 0 0 0; font-size: 12px; opacity: 0.8;">
              📞 07 66 47 77 46 | 📧 Laudare.iledere@gmail.com
            </p>
          </div>
        </div>
      </body>
      </html>
    `;
  }
  
  // Formatage email confirmation utilisateur
  private formatUserConfirmation(submission: EmailSubmission): string {
    const { formType } = submission;
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Confirmation - Les Hirondelles</title>
      </head>
      <body style="font-family: Arial, sans-serif; margin: 0; padding: 0; background-color: #f5f5f5;">
        <div style="max-width: 600px; margin: 0 auto; background-color: white; border: 1px solid #ddd;">
          <!-- Header -->
          <div style="background: #004003; color: white; padding: 20px; text-align: center;">
            <h1 style="margin: 0; font-size: 24px;">✅ Demande Reçue</h1>
            <p style="margin: 10px 0 0 0; opacity: 0.9; font-size: 16px;">Les Hirondelles</p>
          </div>
          
          <!-- Content -->
          <div style="padding: 20px;">
            <p style="font-size: 16px; line-height: 1.5;">Bonjour,</p>
            
            <p style="font-size: 16px; line-height: 1.5;">
              Nous avons bien reçu votre <strong>${formType.toLowerCase()}</strong> et vous remercions de votre intérêt pour Les Hirondelles.
            </p>
            
            <!-- Délai de réponse -->
            <div style="background: #f0f8f0; padding: 15px; border-radius: 5px; border-left: 4px solid #004003; margin: 20px 0;">
              <p style="margin: 0; font-size: 16px;"><strong>⏱️ Délai de réponse:</strong> Nous vous recontacterons sous 24 heures (jours ouvrés).</p>
            </div>
            
            <!-- Contact direct -->
            <div style="background: #f9f9f9; padding: 15px; border-radius: 5px; margin: 20px 0; text-align: center;">
              <p style="margin: 0 0 10px 0; font-size: 16px; font-weight: bold;">📞 Contact direct</p>
              <p style="margin: 0; font-size: 16px;">Téléphone: <strong>07 66 47 77 46</strong></p>
              <p style="margin: 0; font-size: 16px;">Email: <strong>Laudare.iledere@gmail.com</strong></p>
              <p style="margin: 10px 0 0 0; font-size: 14px; color: #666;">Disponible tous les jours de 9h à 19h</p>
            </div>
            
            <p style="font-size: 16px; line-height: 1.5;">
              Merci pour votre confiance,<br>
              <strong>L'équipe Les Hirondelles</strong>
            </p>
          </div>
          
          <!-- Footer -->
          <div style="background: #004003; color: white; padding: 15px; text-align: center;">
            <p style="margin: 0; font-size: 14px;">Les Hirondelles - Location de vélos sur la côte atlantique</p>
            <p style="margin: 5px 0 0 0; font-size: 12px; opacity: 0.8;">30 rue Gambetta, 17590 Ars-en-Ré, France</p>
          </div>
        </div>
      </body>
      </html>
    `;
  }
  
  // Formatage des champs du formulaire
  private formatFormFields(formData: Record<string, any>): string {
    return Object.entries(formData)
      .filter(([_, value]) => value !== null && value !== undefined && value !== '')
      .map(([key, value]) => {
        const label = this.getFieldLabel(key);
        const formattedValue = Array.isArray(value) ? value.join(', ') : String(value);
        return `<p style="margin: 8px 0; padding: 8px; background: #f8f8f8; border-radius: 3px;"><strong>${label}:</strong> ${formattedValue}</p>`;
      })
      .join('');
  }
  
  // Labels des champs
  private getFieldLabel(fieldKey: string): string {
    const labels: Record<string, string> = {
      'nom': 'Nom',
      'prenom': 'Prénom', 
      'email': 'Email',
      'telephone': 'Téléphone',
      'adresse': 'Adresse',
      'entreprise': 'Entreprise',
      'fonction': 'Fonction',
      'typeVelo': 'Type de vélo',
      'nombreVelos': 'Nombre de vélos',
      'dateDebut': 'Date début',
      'dateFin': 'Date fin',
      'zone': 'Zone',
      'camping': 'Camping',
      'bikeId': 'ID Vélo',
      'severity': 'Gravité',
      'category': 'Catégorie',
      'urgency': 'Urgence',
      'priority': 'Priorité',
      'message': 'Message',
      'description': 'Description',
      'sujet': 'Sujet',
      'operatorName': 'Nom opérateur',
      'operatorPhone': 'Téléphone opérateur',
      'contactName': 'Nom contact',
      'contactPhone': 'Téléphone contact',
      'timeSlot': 'Créneau horaire',
      'bikeType': 'Type de vélo',
      'sizeHeight': 'Taille/Hauteur',
      'budgetRange': 'Budget',
      'comments': 'Commentaires'
    };
    
    return labels[fieldKey] || fieldKey.charAt(0).toUpperCase() + fieldKey.slice(1);
  }
  
  // Sujets d'email
  private getEmailSubject(formType: string): string {
    const subjects: Record<string, string> = {
      'Contact': '📞 Nouveau message de contact - Les Hirondelles',
      'Demande de livraison': '🚚 Nouvelle demande de livraison vélo - Les Hirondelles',
      'Demande saisonnier': '👷 Nouvelle demande saisonnier/étudiant - Les Hirondelles',
      'Demande groupe': '👥 Nouvelle demande groupe/séminaire - Les Hirondelles',
      'Demande professionnelle': '🏢 Nouvelle demande professionnelle - Les Hirondelles',
      'Ticket technique': '🔧 Nouveau ticket technique - Les Hirondelles',
      'Demande congé': '📅 Nouvelle demande de congé - Les Hirondelles',
      'Signalement tâche': '📋 Nouvelle tâche signalée - Les Hirondelles',
      'Incident partenaire': '🚨 Nouvel incident partenaire - Les Hirondelles',
      'Demande intervention': '🔧 Nouvelle demande d\'intervention - Les Hirondelles',
      'Demande vélo personnalisé': '🚲 Demande vélo personnalisé - Les Hirondelles',
      'Demande borne réparation': '⚙️ Demande borne auto-réparation - Les Hirondelles'
    };
    
    return subjects[formType] || `📧 Nouvelle demande: ${formType} - Les Hirondelles`;
  }
  
  // Conversion HTML vers texte simple
  private htmlToText(html: string): string {
    return html
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/\s+/g, ' ')
      .trim();
  }
  
  // Méthode pour récupérer toutes les soumissions (admin/debug)
  public getAllSubmissions(): any[] {
    try {
      const submissionIds = JSON.parse(localStorage.getItem('all_form_submissions') || '[]');
      return submissionIds.map((id: string) => {
        const data = localStorage.getItem(`submission_${id}`);
        return data ? JSON.parse(data) : null;
      }).filter(Boolean);
    } catch (error) {
      console.warn('Impossible de récupérer les soumissions:', error);
      return [];
    }
  }
  
  // Méthode pour vider le cache des soumissions
  public clearSubmissions(): void {
    try {
      const submissionIds = JSON.parse(localStorage.getItem('all_form_submissions') || '[]');
      submissionIds.forEach((id: string) => {
        localStorage.removeItem(`submission_${id}`);
      });
      localStorage.removeItem('all_form_submissions');
      console.log('✅ Cache des soumissions vidé');
    } catch (error) {
      console.warn('Erreur lors du nettoyage:', error);
    }
  }
}

// Instance singleton
export const brevoEmailService = new BrevoEmailService();

// Types pour export
export type { EmailSubmission, EmailResponse };

// Fonctions utilitaires pour les formulaires existants
export const submitContactForm = (formData: Record<string, any>, userEmail?: string) => {
  return brevoEmailService.submitForm({
    formType: 'Contact',
    formData,
    userEmail,
    timestamp: new Date().toISOString()
  });
};

export const submitDeliveryRequest = (formData: Record<string, any>, userEmail?: string) => {
  return brevoEmailService.submitForm({
    formType: 'Demande de livraison',
    formData,
    userEmail,
    timestamp: new Date().toISOString()
  });
};

export const submitSeasonalRequest = (formData: Record<string, any>, userEmail?: string) => {
  return brevoEmailService.submitForm({
    formType: 'Demande saisonnier',
    formData,
    userEmail,
    timestamp: new Date().toISOString()
  });
};

export const submitGroupRequest = (formData: Record<string, any>, userEmail?: string) => {
  return brevoEmailService.submitForm({
    formType: 'Demande groupe',
    formData,
    userEmail,
    timestamp: new Date().toISOString()
  });
};

export const submitProfessionalRequest = (formData: Record<string, any>, userEmail?: string) => {
  return brevoEmailService.submitForm({
    formType: 'Demande professionnelle',
    formData,
    userEmail,
    timestamp: new Date().toISOString()
  });
};

export const submitTechnicalTicket = (formData: Record<string, any>, userEmail?: string) => {
  return brevoEmailService.submitForm({
    formType: 'Ticket technique',
    formData,
    userEmail,
    timestamp: new Date().toISOString()
  });
};

export const submitLeaveRequest = (formData: Record<string, any>, userEmail?: string) => {
  return brevoEmailService.submitForm({
    formType: 'Demande congé',
    formData,
    userEmail,
    timestamp: new Date().toISOString()
  });
};

export const submitTaskReport = (formData: Record<string, any>, userEmail?: string) => {
  return brevoEmailService.submitForm({
    formType: 'Signalement tâche',
    formData,
    userEmail,
    timestamp: new Date().toISOString()
  });
};

export const submitPartnerIncident = (formData: Record<string, any>, userEmail?: string) => {
  return brevoEmailService.submitForm({
    formType: 'Incident partenaire',
    formData,
    userEmail,
    timestamp: new Date().toISOString()
  });
};

export const submitInterventionRequest = (formData: Record<string, any>, userEmail?: string) => {
  return brevoEmailService.submitForm({
    formType: 'Demande intervention',
    formData,
    userEmail,
    timestamp: new Date().toISOString()
  });
};

export const submitCustomBikeRequest = (formData: Record<string, any>, userEmail?: string) => {
  return brevoEmailService.submitForm({
    formType: 'Demande vélo personnalisé',
    formData,
    userEmail,
    timestamp: new Date().toISOString()
  });
};

export const submitRepairStationRequest = (formData: Record<string, any>, userEmail?: string) => {
  return brevoEmailService.submitForm({
    formType: 'Demande borne réparation',
    formData,
    userEmail,
    timestamp: new Date().toISOString()
  });
};

// Fonction pour récupérer toutes les soumissions (pour debug/admin)
export const getAllFormSubmissions = () => {
  return brevoEmailService.getAllSubmissions();
};

// Fonction pour vider le cache (pour admin)
export const clearAllSubmissions = () => {
  brevoEmailService.clearSubmissions();
};